<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Customer Integration Form Data Loading...
  </div>

  <div v-else class="animated fadeIn">
    <b-form @submit.prevent="onSubmit">
      <b-form-group label="Select Integration" :label-cols="3" class="mb-3">
        <b-form-select
          v-model="$v.form_data.integration_id.$model"
          :options="integration_options"
          value-field="id"
          text-field="name"
          :state="!$v.form_data.integration_id.$dirty ? null : !$v.form_data.integration_id.$error"
        >
          <template #first>
            <option :value="null" disabled>
              Please select an integration
            </option>
          </template>
        </b-form-select>
        <b-form-invalid-feedback v-if="$v.form_data.integration_id.$dirty && !$v.form_data.integration_id.required">
          Integration selection is required.
        </b-form-invalid-feedback>
      </b-form-group>

      <div v-if="selectedIntegration && selectedIntegration.config && selectedIntegration.config.config" class="mt-3">
        <!-- Emails Configuration -->
        <div v-if="hasConfigField('config.emails')" key="email-config">
          <b-form-group
            label="Email Addresses"
            :label-cols="3"
            class="mb-3"
            :state="!$v.form_data.config.emails.$dirty ? null : !$v.form_data.config.emails.$error"
          >
            <div v-for="(v, index) in $v.form_data.config.emails.$each.$iter" :key="index" class="mb-2">
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model.trim="v.value.$model"
                  type="email"
                  placeholder="email@example.com"
                  class="mr-2"
                  :state="!v.value.$dirty ? null : !v.value.$error"
                />
                <b-button
                  variant="danger"
                  @click="removeEmailField(index)"
                  :disabled="isConfigFieldRequired('config.emails') && form_data.config.emails.length <= 1"
                >
                  <i class="fa fa-trash" />
                </b-button>
              </div>
              <b-form-invalid-feedback v-if="v.value.$dirty && !v.value.required">
                Email address is required.
              </b-form-invalid-feedback>
              <b-form-invalid-feedback v-else-if="v.value.$dirty && !v.value.email">
                Please enter a valid email address.
              </b-form-invalid-feedback>
            </div>
            <b-form-invalid-feedback v-if="$v.form_data.config.emails.$dirty && $v.form_data.config.emails.$error">
              At least one email address is required.
            </b-form-invalid-feedback>
            <b-button variant="success" @click="addEmailField" class="mt-2">
              <i class="fa fa-plus" /> Add Email
            </b-button>
          </b-form-group>
        </div>

        <!-- Growers Configuration -->
        <div v-if="hasConfigField('config.growers')" key="growers-config">
          <b-form-group
            label="Growers"
            :label-cols="3"
            class="mb-3"
            :state="!$v.form_data.config.growers.$dirty ? null : !$v.form_data.config.growers.$error"
          >
            <div v-for="(v, index) in $v.form_data.config.growers.$each.$iter" :key="index" class="mb-2">
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model.trim="v.value.$model"
                  type="text"
                  placeholder="Grower name"
                  class="mr-2"
                  :state="!v.value.$dirty ? null : !v.value.$error"
                />
                <b-button
                  variant="danger"
                  @click="removeGrowerField(index)"
                  :disabled="isConfigFieldRequired('config.growers') && form_data.config.growers.length <= 1"
                >
                  <i class="fa fa-trash" />
                </b-button>
              </div>
              <b-form-invalid-feedback v-if="v.value.$dirty && !v.value.required">
                Grower name is required.
              </b-form-invalid-feedback>
            </div>
            <b-button variant="success" @click="addGrowerField" class="mt-2">
              <i class="fa fa-plus" /> Add Grower
            </b-button>
          </b-form-group>
        </div>

        <div v-if="hasConfigField('config.require_inspection')" key="inspection-config">
          <b-form-group
            label="Require Inspection"
            :label-cols="3"
            class="mb-3"
          >
            <b-form-checkbox
              v-model="form_data.config.require_inspection"
              :value="true"
              :unchecked-value="false"
            >
              Require inspection for this integration
            </b-form-checkbox>
          </b-form-group>
        </div>

        <div v-if="hasConfigField('config.require_recommendations')" key="recommendations-config">
          <b-form-group
            label="Require Recommendations"
            :label-cols="3"
            class="mb-3"
          >
            <b-form-checkbox
              v-model="form_data.config.require_recommendations"
              :value="true"
              :unchecked-value="false"
            >
              Require recommendations for this integration
            </b-form-checkbox>
          </b-form-group>
        </div>
      </div>

      <!-- Fallback message when no config is available -->
      <div v-else-if="selectedIntegration" class="mt-3">
        <b-alert show variant="info" :label-cols="3">This integration requires no additional configuration.</b-alert>
      </div>

      <!-- validation error -->
      <b-row v-if="validationError">
        <b-col>
          <b-alert variant="danger" show>
            <i class="fa fa-exclamation-triangle" /> Please fill out all required fields.
          </b-alert>
        </b-col>
      </b-row>

      <b-row v-if="error">
        <b-col>
          <b-alert variant="danger" show>
            <i class="fa fa-exclamation-triangle" /> {{ error }}
          </b-alert>
        </b-col>
      </b-row>

      <div class="clearfix mb-5">
        <b-button variant="success" class="float-right" :disabled="submittingForm" @click="onSubmit">
          <i v-if="submittingForm" class="fal fa-spinner fa-spin"></i>
          <span v-else>Save</span>
        </b-button>
      </div>
    </b-form>
  </div>
</template>

<script>
import IntegrationCollection from '@/collections/result/IntegrationCollection'
import { validationMixin } from 'vuelidate'
import { required, email, requiredIf, minLength } from 'vuelidate/lib/validators'

export default {
  name: 'CustomerIntegrationForm',
  mixins: [validationMixin],
  props: {
    submittingForm: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    customer_integration: {
      type: Object,
      default () {
        return null
      }
    }
  },
  data () {
    return {
      loading: false,

      form_data: {
        integration_id: null,
        config: {},
      },

      integration_options: [],

      validationError: false
    }
  },
  validations: {
    form_data: {
      integration_id: { required },
      config: {
        emails: {
          required: requiredIf(function () {
            return this.isConfigFieldRequired('config.emails')
          }),
          minLength: minLength(1),
          $each: {
            value: {
              required: requiredIf(function () {
                return this.isConfigFieldRequired('config.emails.*')
              }),
              email
            }
          }
        },
        growers: {
          required: requiredIf(function () {
            return this.isConfigFieldRequired('config.growers')
          }),
          minLength: minLength(0),
          $each: {
            value: {
              required: requiredIf(function () {
                return this.isConfigFieldRequired('config.growers.*')
              })
            }
          }
        }
      }
    }
  },
  computed: {
    selectedIntegration () {
      if (this.form_data.integration_id && this.integration_options.length) {
        return this.integration_options.find(opt => opt.id === this.form_data.integration_id)
      }
      return null
    },
    selectedIntegrationType () {
      return this.selectedIntegration ? this.selectedIntegration.type : null
    }
  },
  watch: {
    selectedIntegration (newIntegration, oldIntegration) {
      if (newIntegration && newIntegration.config && newIntegration.config.config) {
        this.initializeConfig()
      } else if (oldIntegration) {
        this.$set(this.form_data, 'config', {})
      }
    }
  },
  mounted () {
    this.fetchIntegrations()
    this.initForm()
  },
  methods: {
    hasConfigField (fieldName) {
      return this.selectedIntegration &&
             this.selectedIntegration.config &&
             this.selectedIntegration.config.config &&
             this.selectedIntegration.config.hasOwnProperty(fieldName)
    },

    isConfigFieldRequired (fieldName) {
      if (!this.hasConfigField(fieldName)) return false
      const rules = this.selectedIntegration.config[fieldName]
      return Array.isArray(rules) && rules.includes('required')
    },

    initializeConfig () {
      if (!this.selectedIntegration || !this.selectedIntegration.config || !this.selectedIntegration.config.config) {
        return
      }

      const config = { ...this.form_data.config }

      // Initialize emails array if required
      if (this.hasConfigField('config.emails') && !config.emails && this.isConfigFieldRequired('config.emails')) {
        config.emails = [{ value: '' }]
      }

      // Initialize growers array if required
      if (this.hasConfigField('config.growers') && !config.growers && this.isConfigFieldRequired('config.growers')) {
        config.growers = [{ value: '' }]
      }

      // Initialize boolean fields
      if (this.hasConfigField('config.require_inspection') && config.require_inspection === undefined) {
        config.require_inspection = false
      }

      if (this.hasConfigField('config.require_recommendations') && config.require_recommendations === undefined) {
        config.require_recommendations = false
      }

      this.$set(this.form_data, 'config', config)
    },

    fetchIntegrations () {
      this.loading = true

      const collection = new IntegrationCollection()
      collection.fetch().then(() => {
        this.integration_options = collection.map(integration => ({
          id: integration.id,
          name: [integration.name, integration.description, integration.type && _.startCase(integration.type)]
            .filter(value => value != null && value !== '')
            .join(' - ') || 'Unnamed Integration',
          type: integration.type,
          config: integration.config || null
        }))
        this.loading = false
      }).catch(() => {
        this.loading = false
        this.quickDangerToast('Error', 'Unable to load integrations')
      })
    },

    addEmailField () {
      if (!this.form_data.config.emails) {
        this.$set(this.form_data.config, 'emails', [])
      }
      this.form_data.config.emails.push({ value: '' })
    },

    removeEmailField (index) {
      if (this.form_data.config.emails && this.form_data.config.emails.length >= 1) {
        this.form_data.config.emails.splice(index, 1)
      }
    },

    addGrowerField () {
      if (!this.form_data.config.growers) {
        this.$set(this.form_data.config, 'growers', [])
      }
      this.form_data.config.growers.push({ value: '' })
    },

    removeGrowerField (index) {
      if (this.form_data.config.growers && this.form_data.config.growers.length >= 1) {
        this.form_data.config.growers.splice(index, 1)
      }
    },

    onSubmit () {
      this.validationError = false
      this.$v.$touch()

      if (this.$v.$invalid) {
        this.validationError = true
        return
      }

      // Transform form_data before emitting
      const transformedData = { ...this.form_data }

      // Convert emails from array of objects to array of strings
      if (transformedData.config && transformedData.config.emails) {
        transformedData.config = {
          ...transformedData.config,
          emails: transformedData.config.emails.map(email => email.value),
          growers: transformedData.config.growers ? transformedData.config.growers.map(grower => grower.value) : []
        }
      }

      this.$emit('submit', transformedData)
    },

    initForm () {
      // Edit form
      if (this.customer_integration) {
        this.form_data.integration_id = this.customer_integration.integration_id

        if (this.customer_integration.config) {
          const config = { ...this.customer_integration.config }

          if (config.emails && Array.isArray(config.emails)) {
            config.emails = config.emails.map(email => ({ value: email }))
          }

          if (config.growers && Array.isArray(config.growers)) {
            config.growers = config.growers.map(grower => ({ value: grower }))
          }

          this.form_data.config = config
        } else {
          this.form_data.config = {}
        }
      }
    },

    resetForm () {
      this.form_data.integration_id = null
      this.form_data.config = {}
      this.$v.$reset()
      this.validationError = false
    }
  }
}
</script>
